import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import parseISO from "date-fns/parseISO";
import format from "date-fns/format";
import getHours from "date-fns/getHours";
import getMinutes from "date-fns/getMinutes";
import hoursToMinutes from "date-fns/hoursToMinutes";
import { createSupabaseClient } from "../../../hooks/useSupabase";
import {
  getTimezoneOffsetMillis,
  UTCDateTimeToTimezone,
} from "../../../common/dateHelpers";

const SLOT_INTERVAL = 30;
const DEFAULT_SLOT_START = 420;
const DEFAULT_SLOT_END = 1200;

/**
 * Rounds minutes up to the nearest 30-min slot boundary.
 * If minutes is already on a boundary (:00 or :30), returns the NEXT slot.
 * Disable the slot of the appointment start time and everything before it,
 * plus the next slot if the time is not on :00 or :30.
 */
const getMinimumAllowedSlot = (
  appointmentMinutesFromMidnight: number,
): number => {
  const remainder = appointmentMinutesFromMidnight % SLOT_INTERVAL;
  if (remainder === 0) {
    return appointmentMinutesFromMidnight + SLOT_INTERVAL;
  }
  return appointmentMinutesFromMidnight - remainder + SLOT_INTERVAL * 2;
};

const STAT_APPOINTMENT_STATUSES = [
  "confirmed",
  "checked_in",
  "studies_unread",
  "studies_read",
  "intake_complete",
] as const;

type StatAppointment = {
  centerId: string;
  centerTimezone: string | null;
  startedAtUTC: string;
  radiologySpecialty: string | null;
};

type RawStatRow = {
  center_id: string;
  center: { id: string; timezone: string | null };
  time_slot: { started_at: string };
  appointment_offerings: { offering: { radiology_specialty: string | null } }[];
};

type OtherRadiologist = {
  centerUserId: string;
  specialties: string[];
};

type RadiologistCutoffRow = {
  centerUserId: string;
  centerId: string;
  date: string;
  type: "available" | "unavailable" | "cutoff_time";
  cutoffMinutesFromMidnight: number | null;
};

export type DayConstraint = {
  minSlotMinutes: number;
  unavailableDisabled: boolean;
  slotStart: number;
  slotEnd: number;
};

export type StatConstraints = Record<string, DayConstraint>;

const canOtherRadCover = (
  rad: OtherRadiologist,
  specialty: string,
  centerId: string,
  dateISO: string,
  appointmentMinutes: number,
  cutoffs: RadiologistCutoffRow[],
): boolean => {
  if (!rad.specialties.includes(specialty)) return false;
  const cutoff = cutoffs.find(
    (c) =>
      c.centerUserId === rad.centerUserId &&
      c.centerId === centerId &&
      c.date === dateISO,
  );
  if (!cutoff || cutoff.type === "available") return true;
  if (cutoff.type === "unavailable") return false;
  return (
    cutoff.cutoffMinutesFromMidnight !== null &&
    cutoff.cutoffMinutesFromMidnight > appointmentMinutes
  );
};

const computeConstraints = (
  appointments: StatAppointment[],
  weekStartISO: string,
  weekEndISO: string,
  currentUserSpecialties: string[],
  otherRadiologists: OtherRadiologist[],
  otherRadCutoffs: RadiologistCutoffRow[],
): StatConstraints => {
  const inWeek = appointments.filter((appt) => {
    const startedAtUTC = parseISO(appt.startedAtUTC);
    const offsetMillis = getTimezoneOffsetMillis(appt.centerTimezone);
    const startedAtLocal = UTCDateTimeToTimezone(startedAtUTC, offsetMillis);
    const dateKey = format(startedAtLocal, "yyyy-MM-dd");
    return dateKey >= weekStartISO && dateKey < weekEndISO;
  });

  return inWeek.reduce<StatConstraints>((result, appt) => {
    const { radiologySpecialty } = appt;
    if (
      !radiologySpecialty ||
      !currentUserSpecialties.includes(radiologySpecialty)
    ) {
      return result;
    }

    const startedAtUTC = parseISO(appt.startedAtUTC);
    const offsetMillis = getTimezoneOffsetMillis(appt.centerTimezone);
    const startedAtLocal = UTCDateTimeToTimezone(startedAtUTC, offsetMillis);

    const dateKey = format(startedAtLocal, "yyyy-MM-dd");
    const appointmentMinutes =
      hoursToMinutes(getHours(startedAtLocal)) + getMinutes(startedAtLocal);

    const someoneElseCan = otherRadiologists.some((rad) =>
      canOtherRadCover(
        rad,
        radiologySpecialty,
        appt.centerId,
        dateKey,
        appointmentMinutes,
        otherRadCutoffs,
      ),
    );

    if (someoneElseCan) return result;

    const slotStart = DEFAULT_SLOT_START;
    const slotEnd = DEFAULT_SLOT_END;

    const minSlot = getMinimumAllowedSlot(appointmentMinutes);
    const existing = result[dateKey];

    const newSlotStart = existing
      ? Math.min(existing.slotStart, slotStart)
      : slotStart;
    const newSlotEnd = existing ? Math.max(existing.slotEnd, slotEnd) : slotEnd;

    if (!existing || minSlot > existing.minSlotMinutes) {
      return {
        ...result,
        [dateKey]: {
          minSlotMinutes: minSlot,
          unavailableDisabled: true,
          slotStart: newSlotStart,
          slotEnd: newSlotEnd,
        },
      };
    }

    if (
      newSlotStart !== existing.slotStart ||
      newSlotEnd !== existing.slotEnd
    ) {
      return {
        ...result,
        [dateKey]: {
          ...existing,
          slotStart: newSlotStart,
          slotEnd: newSlotEnd,
        },
      };
    }
    return result;
  }, {});
};

/**
 * Computes per-day disabled select values for a radiologist-only user.
 * Fetches STAT appointments in the user's assigned centers for the week,
 * checks if no other radiologist with the required specialty can cover,
 * and disables conflicting time slots.
 */
const useStatConstraints = (params: {
  centerIds: string[];
  currentUserId: string;
  weekStart: Date;
  weekEnd: Date;
  enabled: boolean;
}) => {
  const { centerIds, currentUserId, weekStart, weekEnd, enabled } = params;
  const supabase = useMemo(() => createSupabaseClient(), []);

  type QueryData = {
    constraints: StatConstraints;
  };

  const { data } = useQuery<QueryData>({
    queryKey: [
      "stat-constraints",
      { centerIds, currentUserId, weekStart, weekEnd },
    ],
    queryFn: async () => {
      const weekStartISO = format(weekStart, "yyyy-MM-dd");
      const weekEndISO = format(weekEnd, "yyyy-MM-dd");

      const { data: userSpecialtiesData, error: userSpecialtiesError } =
        await supabase
          .from("radiologist_specialties")
          .select("specialty")
          .in("center_id", centerIds)
          .eq("center_user_id", currentUserId);

      if (userSpecialtiesError) throw new Error(userSpecialtiesError.message);

      const currentUserSpecialties: string[] = (userSpecialtiesData ?? []).map(
        (r) => r.specialty,
      );

      if (currentUserSpecialties.length === 0) {
        return { constraints: {} };
      }

      const { data: apptData, error: apptError } = await supabase
        .from("appointments")
        .select(
          "center_id, center:centers!inner(id, timezone), time_slot:center_time_slots!inner(started_at), appointment_offerings!inner(offering:center_offerings!inner(radiology_specialty))",
        )
        .in("center_id", centerIds)
        .eq("is_stat", true)
        .in("status", STAT_APPOINTMENT_STATUSES);

      if (apptError) throw new Error(apptError.message);

      const appointments: StatAppointment[] = (
        apptData as unknown as RawStatRow[]
      ).flatMap((row) =>
        row.appointment_offerings.map((ao) => ({
          centerId: row.center_id,
          centerTimezone: row.center?.timezone ?? null,
          startedAtUTC: row.time_slot.started_at,
          radiologySpecialty: ao.offering?.radiology_specialty ?? null,
        })),
      );

      const { data: otherSpecialtiesData, error: otherSpecialtiesError } =
        await supabase
          .from("radiologist_specialties")
          .select("center_user_id, specialty")
          .in("center_id", centerIds)
          .neq("center_user_id", currentUserId);

      if (otherSpecialtiesError) throw new Error(otherSpecialtiesError.message);

      const otherRadiologists = (otherSpecialtiesData ?? []).reduce<
        OtherRadiologist[]
      >((acc, row) => {
        const existing = acc.find((r) => r.centerUserId === row.center_user_id);
        if (existing) {
          if (!existing.specialties.includes(row.specialty)) {
            existing.specialties.push(row.specialty);
          }
          return acc;
        }
        return [
          ...acc,
          { centerUserId: row.center_user_id, specialties: [row.specialty] },
        ];
      }, []);

      if (otherRadiologists.length === 0) {
        return {
          constraints: computeConstraints(
            appointments,
            weekStartISO,
            weekEndISO,
            currentUserSpecialties,
            [],
            [],
          ),
        };
      }

      const otherRadIds = otherRadiologists.map((r) => r.centerUserId);

      const { data: cutoffData, error: cutoffError } = await supabase
        .from("radiologist_read_cutoffs")
        .select(
          "center_id, center_user_id, date, type, cutoff_minutes_from_midnight",
        )
        .in("center_id", centerIds)
        .in("center_user_id", otherRadIds)
        .gte("date", weekStartISO)
        .lt("date", weekEndISO);

      if (cutoffError) throw new Error(cutoffError.message);

      type RawCutoffRow = {
        center_id: string;
        center_user_id: string;
        date: string;
        type: "available" | "unavailable" | "cutoff_time";
        cutoff_minutes_from_midnight: number | null;
      };

      const otherRadCutoffs: RadiologistCutoffRow[] = (
        (cutoffData ?? []) as unknown as RawCutoffRow[]
      ).map((row) => ({
        centerUserId: row.center_user_id,
        centerId: row.center_id,
        date: row.date,
        type: row.type,
        cutoffMinutesFromMidnight: row.cutoff_minutes_from_midnight,
      }));

      return {
        constraints: computeConstraints(
          appointments,
          weekStartISO,
          weekEndISO,
          currentUserSpecialties,
          otherRadiologists,
          otherRadCutoffs,
        ),
      };
    },
    enabled: enabled && centerIds.length > 0,
  });

  const constraints = data?.constraints ?? {};

  /**
   * Returns the hardcoded slot bounds: 7:30am–7:30pm (first and last
   * timeslots of the 7am–8pm window are excluded).
   */
  const getSlotBounds = (): { slotStart: number; slotEnd: number } => ({
    slotStart: DEFAULT_SLOT_START + SLOT_INTERVAL,
    slotEnd: DEFAULT_SLOT_END - SLOT_INTERVAL,
  });

  /**
   * Returns the list of disabled select values for a given date.
   * Returns [] if no STAT constraint exists for that date.
   */
  const getDisabledValues = (dateISO: string): string[] => {
    const constraint = constraints[dateISO];
    if (!constraint) return [];

    const firstVisibleSlot = constraint.slotStart + SLOT_INTERVAL;
    const slotCount = Math.max(
      0,
      Math.ceil((constraint.minSlotMinutes - firstVisibleSlot) / SLOT_INTERVAL),
    );
    const disabledSlots = Array.from({ length: slotCount }, (_, i) =>
      String(firstVisibleSlot + i * SLOT_INTERVAL),
    );

    const disabledSpecial = constraint.unavailableDisabled
      ? ["unavailable"]
      : [];

    return [...disabledSpecial, ...disabledSlots];
  };

  return { constraints, getDisabledValues, getSlotBounds };
};

export default useStatConstraints;
